import { createStore, type StoreApi } from 'zustand/vanilla';

import type {
  APIChannel,
  APIGuildMember,
  APIMessage,
  APIUser,
} from '../contracts/chat-types.js';
import type { GatewayStatusInfo } from '../gateway/gateway-client.js';
import {
  type FetchQuery,
  mergeFetched,
  removeMessages,
  trimToNewest,
  upsertMessage,
} from './message-lists.js';
import { applyDispatch } from './reducer.js';
import {
  type ChatState,
  initialChatState,
  type PendingMessage,
  type Snowflake,
} from './state.js';

export type ChatActions = {
  /** Evento do gateway. */
  dispatch(t: string, d: unknown): void;
  setConnection(info: GatewayStatusInfo): void;
  /** Mensagens da REST (mais nova primeiro, como vêm). */
  messagesLoaded(
    channelId: Snowflake,
    messages: APIMessage[],
    query: FetchQuery,
  ): void;
  /** Resposta da REST (enviar, editar): entra como o evento entraria. */
  messageSaved(message: APIMessage): void;
  messageRemoved(channelId: Snowflake, messageId: Snowflake): void;
  /** Ao sair de um canal: guarda só as mais novas. */
  trimMessages(channelId: Snowflake, keep: number): void;
  addPending(message: PendingMessage): void;
  updatePending(
    channelId: Snowflake,
    nonce: string,
    patch: Partial<PendingMessage>,
  ): void;
  removePending(channelId: Snowflake, nonce: string): void;
  /** Leitura otimista (o `MESSAGE_ACK` das outras sessões confirma). */
  setRead(
    channelId: Snowflake,
    lastMessageId: Snowflake | null,
    mentionCount: number,
  ): void;
  upsertChannel(channel: APIChannel): void;
  upsertUsers(users: APIUser[]): void;
  upsertMember(guildId: Snowflake, member: APIGuildMember): void;
  /** Notas dos perfis (`GET /users/@me/notes`). */
  notesLoaded(notes: Record<Snowflake, string>): void;
  /** Tira quem parou de digitar (chamar a cada segundo enquanto a tela mostra). */
  pruneTyping(now?: number): void;
  /** Sair da conta: esquece tudo. */
  reset(): void;
};

export type ChatStoreState = ChatState & ChatActions;
export type ChatStore = StoreApi<ChatStoreState>;

/**
 * A store do chat (zustand, sem React): o estado normalizado e as ações. A
 * interface assina com `useStore(store, seletor)` do lado dela (web e
 * celular), usando os seletores de `selectors.ts`.
 */
export function createChatStore(
  now: () => number = () => Date.now(),
): ChatStore {
  return createStore<ChatStoreState>()((set) => ({
    ...initialChatState(),

    dispatch: (t, d) => set((state) => applyDispatch(state, t, d, now())),

    setConnection: (info) =>
      set((state) => ({
        connection: {
          status: info.status,
          retryInMs: info.retryInMs ?? null,
          fatalCode:
            info.status === 'closed'
              ? (info.closeCode ?? null)
              : state.connection.fatalCode,
        },
      })),

    messagesLoaded: (channelId, messages, query) =>
      set((state) => ({
        messages: {
          ...state.messages,
          [channelId]: mergeFetched(state.messages[channelId], messages, query),
        },
        users: {
          ...state.users,
          ...Object.fromEntries(
            messages
              .filter((message) => !message.webhook_id)
              .map((message) => [message.author.id, message.author]),
          ),
        },
      })),

    messageSaved: (message) =>
      set((state) => {
        const exists = state.messages[message.channel_id]?.byId[message.id];
        if (exists) {
          return {
            messages: {
              ...state.messages,
              [message.channel_id]: upsertMessage(
                state.messages[message.channel_id],
                message,
                { append: false },
              )!,
            },
          };
        }
        // Ainda não veio pelo gateway: aplica como se tivesse vindo.
        return applyDispatch(state, 'MESSAGE_CREATE', message, now());
      }),

    messageRemoved: (channelId, messageId) =>
      set((state) => {
        const list = removeMessages(state.messages[channelId], [messageId]);
        return list === state.messages[channelId]
          ? {}
          : { messages: { ...state.messages, [channelId]: list! } };
      }),

    trimMessages: (channelId, keep) =>
      set((state) => {
        const list = state.messages[channelId];
        if (!list) return {};
        const trimmed = trimToNewest(list, keep);
        return trimmed === list
          ? {}
          : { messages: { ...state.messages, [channelId]: trimmed } };
      }),

    addPending: (message) =>
      set((state) => ({
        pending: {
          ...state.pending,
          [message.channelId]: [
            ...(state.pending[message.channelId] ?? []).filter(
              (item) => item.nonce !== message.nonce,
            ),
            message,
          ],
        },
      })),

    updatePending: (channelId, nonce, patch) =>
      set((state) => ({
        pending: {
          ...state.pending,
          [channelId]: (state.pending[channelId] ?? []).map((item) =>
            item.nonce === nonce ? { ...item, ...patch } : item,
          ),
        },
      })),

    removePending: (channelId, nonce) =>
      set((state) => ({
        pending: {
          ...state.pending,
          [channelId]: (state.pending[channelId] ?? []).filter(
            (item) => item.nonce !== nonce,
          ),
        },
      })),

    setRead: (channelId, lastMessageId, mentionCount) =>
      set((state) => ({
        readStates: {
          ...state.readStates,
          [channelId]: {
            id: channelId,
            last_message_id: lastMessageId,
            mention_count: mentionCount,
            last_pin_timestamp:
              state.readStates[channelId]?.last_pin_timestamp ?? null,
          },
        },
      })),

    notesLoaded: (notes) => set({ notes }),

    upsertChannel: (channel) =>
      set((state) => applyDispatch(state, 'CHANNEL_UPDATE', channel, now())),

    upsertUsers: (users) =>
      set((state) => ({
        users: {
          ...state.users,
          ...Object.fromEntries(users.map((user) => [user.id, user])),
        },
      })),

    upsertMember: (guildId, member) =>
      set((state) =>
        applyDispatch(
          state,
          'GUILD_MEMBER_UPDATE',
          { ...member, guild_id: guildId },
          now(),
        ),
      ),

    pruneTyping: (at) =>
      set((state) => {
        const moment = at ?? now();
        let changed = false;
        const typing: ChatState['typing'] = {};
        for (const [channelId, people] of Object.entries(state.typing)) {
          const alive = Object.fromEntries(
            Object.entries(people).filter(([, expires]) => expires > moment),
          );
          if (Object.keys(alive).length !== Object.keys(people).length) {
            changed = true;
          }
          if (Object.keys(alive).length) typing[channelId] = alive;
        }
        return changed ? { typing } : {};
      }),

    reset: () => set(initialChatState()),
  }));
}
